import { ReactNode } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TDocsFeedbackType } from 'lendingApi/lendingApi.types';

export const translateDocsFeedbackType = (
  feedbackType: TDocsFeedbackType,
): ReactNode => {
  switch (feedbackType) {
    case 'ILLEGIBLE':
      return (
        <Trans>
          This bank statement is <strong>illegible</strong>. Please upload a new
          document in PDF format that is of sufficient quality.
        </Trans>
      );
    case 'INVALID_DOCUMENT_FORMAT':
      return (
        <Trans>
          This bank statement is <strong>not</strong> available in{' '}
          <strong>PDF format</strong>. Please upload a new document that is in
          its original PDF format, directly from your online banking.
        </Trans>
      );
    case 'PASSWORD_NOT_AVAILABLE':
      return (
        <Trans>
          This bank statement is <strong>password protected</strong>. Please
          upload a new, unprotected PDF document or send us the password via
          email.
        </Trans>
      );
    case 'REFERENCES_MISSING':
      return (
        <Trans>
          This bank statement contains{' '}
          <strong>incomplete transaction references</strong>. Please upload a
          new document in PDF format that includes complete and detailed
          transaction references. Further information on the required references
          can be found{' '}
          <a
            href={t`https://www.iwoca.co.uk/references-missing-link`}
            target="_blank"
            rel="noreferrer"
          >
            here
          </a>
          .
        </Trans>
      );
    case 'BALANCE_FIGURES_MISSING':
      return (
        <Trans>
          This statement does not include the <strong>balance</strong>. Please
          upload a new document in PDF format showing the complete account
          balance at the beginning and end of the transactions.
        </Trans>
      );
    case 'MISSING_TRANSACTIONS':
      return (
        <Trans>
          This bank statement contains <strong>incomplete transactions</strong>.
          Please upload a new document in PDF format showing all transactions
          for the last 90 days.
        </Trans>
      );
    case 'MISSING_APPLICANT_NAME_AND_COMPANY_NAME':
      return (
        <Trans>
          This bank statement does <strong>not</strong> contain your client's{' '}
          <strong>full name</strong> and <strong>company name</strong>. Please
          upload a new document in PDF format that includes this information.
        </Trans>
      );
    case 'MISSING_SUMMARY_PAGE':
      return (
        <Trans>
          This bank statement does not contain a <strong>summary page</strong>.
          Please upload a new document in PDF format that includes the first
          page with the bank's name and logo, the full IBAN, your client's full
          name, and the company name.
        </Trans>
      );
    case 'MISSING_LOGO_AND_IBAN':
      return (
        <Trans>
          This bank statement does not contain the <strong>bank logo</strong>{' '}
          and/or <strong>IBAN</strong>. Please upload a new document in PDF
          format that contains this information.
        </Trans>
      );
    case 'SCREENSHOT':
      return (
        <Trans>
          This bank statement is a <strong>screenshot</strong>. Please upload a
          new document in PDF format that was exported directly from your online
          banking.
        </Trans>
      );
    case 'ACTIVE_LINKS':
      return (
        <Trans>
          This bank statement contains <strong>active links</strong> in the
          transaction lines. Please upload a new document without active links
          in PDF format.
        </Trans>
      );
    case 'INCORRECT_ORDER':
      return (
        <Trans>
          This bank statement shows transactions that are{' '}
          <strong>not in chronological order</strong>. Please upload a new
          document in PDF format that contains the transactions in chronological
          order.
        </Trans>
      );
    case 'BS_WITHOUT_ACTIVE_LINKS':
      return null;
    default:
      return null;
  }
};
